import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Search, Users, X } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface ReportCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

export default function SaleActivityPartySection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const { data: customersRes } = useQuery({
    queryKey: ['customersForSaleActivityParty', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: ReportCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: ReportCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['saleActivityParty', from_date, to_date, selectedCustomer],
    queryFn: () =>
      reportsApi.getSaleActivityParty({
        from_date,
        to_date,
        customer: selectedCustomer || undefined,
      }),
  });

  useEffect(() => {
    setPage(0);
  }, [rows, from_date, to_date, selectedCustomer]);

  const handleExport = () => {
    if (!rows.length) return;
    downloadCsv(
      `sale-activity-party-${from_date}-${to_date}.csv`,
      [
        { key: 'customer_name', label: 'Customer' },
        { key: 'visits', label: 'No. of Visits' },
        { key: 'invoices', label: 'No. of Invoices' },
        { key: 'total_qty', label: 'Total Qty' },
        { key: 'total_purchase', label: 'Total Purchase' },
        { key: 'avg_invoice_value', label: 'Average Invoice Value' },
        { key: 'last_purchase_date', label: 'Last Purchase Date' },
      ],
      rows as Record<string, unknown>[]
    );
  };

  const pageRows = rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              Customer
            </label>
            {selectedCustomer ? (
              <div className="input-field flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-gray-900">{selectedCustomerLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelectCustomer()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Clear customer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder={t('reports.searchByNamePhone')}
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer()}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                    >
                      All Customers
                    </button>
                    {customers.length > 0 ? (
                      customers.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                        >
                          <p className="font-medium text-gray-900">{c.customer_name}</p>
                          {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">No customers found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : rows.length > 0 ? (
          <table className="w-full text-sm text-left min-w-[880px]">
            <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium text-right">No. of Visits</th>
                <th className="px-4 py-3 font-medium text-right">No. of Invoices</th>
                <th className="px-4 py-3 font-medium text-right">Total Qty</th>
                <th className="px-4 py-3 font-medium text-right">Total Purchase</th>
                <th className="px-4 py-3 font-medium text-right">Average Invoice Value</th>
                <th className="px-4 py-3 font-medium">Last Purchase Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((row: any) => (
                <tr key={row.customer} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                  <td className="px-4 py-3 text-right">{row.visits}</td>
                  <td className="px-4 py-3 text-right">{row.invoices}</td>
                  <td className="px-4 py-3 text-right">{row.total_qty}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatMoney(row.total_purchase)}</td>
                  <td className="px-4 py-3 text-right">{formatMoney(row.avg_invoice_value)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                    {row.last_purchase_date
                      ? format(new Date(row.last_purchase_date), 'd MMM yyyy')
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No party activity for the selected filters</p>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={rows.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
